// ★ O SDK do LiveKit, carregado em TEMPO DE EXECUÇÃO — e por que não é
// dependência do `package.json` (spec-009, branch de TESTE).
//
// O vídeo do avatar só chega por WebRTC numa sala LiveKit: o contrato do
// provedor (`StartSessionResponseSchema`) tem `livekit_url` e
// `livekit_client_token` como campos OBRIGATÓRIOS e não oferece nenhum
// caminho HTTP/HLS. Ou seja, falar LiveKit não é escolha — é o único jeito
// de o avatar aparecer.
//
// **Por que não `npm i livekit-client`:**
//
// 1. Esta branch é um TESTE que pode ser descartado inteiro (direção do
//    owner, 18/09/2026: *"se nao ficar bom fazemos somente com gpt live"*).
//    Uma dependência de 420 KB no `package.json` de um repo compartilhado
//    por várias sessões custa a todo mundo, inclusive a quem não vai usar.
// 2. O build é vue-cli 4.5 / webpack 4, e `transpileDependencies` está
//    DESLIGADO (`vue.config.js`). O acorn do webpack 4 não analisa sintaxe
//    ES2020 (`?.`, `??`), que pacotes modernos publicam no bundle ESM — o
//    sintoma seria um erro de PARSE no build, longe daqui.
//
// O bundle UMD é ES5-compatível e se expõe em `globalThis.LivekitClient`,
// então um `<script>` resolve os dois problemas de uma vez, sem tocar no
// build de ninguém.
//
// ⚠️ **`integrity` não é enfeite.** Sem SRI, um CDN comprometido executa
// código arbitrário numa plataforma que mostra carteira de cliente. Com o
// hash, o browser RECUSA o script se um único byte mudar — inclusive se a
// versão for republicada. Trocar de versão é trocar o par
// (`VERSAO`, `INTEGRIDADE`) JUNTO; mudar só um faz o script ser recusado, que
// é exatamente o desfecho certo.
//
// ⚠️ **`crossorigin="anonymous"` é obrigatório com `integrity`** — sem ele o
// browser não consegue verificar o hash de um recurso cross-origin e bloqueia
// o carregamento.
//
// Se o teste for aprovado, a evolução é uma linha de `package.json` e apagar
// este arquivo.

/** Par CONGELADO — ver o ⚠️ acima. Medido em 18/09/2026. */
export const VERSAO = '2.15.7'
export const INTEGRIDADE =
  'sha384-Im1I1B8kg+QAqMWMje7KTj17burxG2bnM4X8//QdlzfKYxi8sQuvsRYwHbU2vyAJ'
export const URL_DO_SDK =
  `https://cdn.jsdelivr.net/npm/livekit-client@${VERSAO}/dist/livekit-client.umd.js`

/** O nome que o bundle UMD publica no escopo global. */
export const GLOBAL = 'LivekitClient'

/**
 * Teto de carga. O bundle tem ~420 KB; numa rede ruim 20 s é generoso, e
 * passado isso é melhor cair no retrato estático do que deixar a pessoa
 * esperando por um vídeo que não vem.
 */
export const MS_ATE_CARREGAR = 20000

// Promessa ÚNICA por carregamento de página: o script é global, e duas
// montagens do componente não podem injetar duas tags. Estado de MÓDULO de
// propósito (não de instância) — é o recurso que é global, não o consumidor.
let _carga = null

/**
 * Carrega o SDK e devolve o namespace dele.
 *
 * @returns {Promise<Object|null>} o `LivekitClient`, ou `null` quando não deu
 *   (sem DOM, script recusado pela integridade, rede, timeout). NUNCA rejeita:
 *   o vídeo é opcional por construção, e uma rejeição aqui derrubaria a
 *   conversa falada junto — que é a parte que importa.
 */
/** Monta a tag, com SRI. Separado só para caber no teto de statements. */
const _montarScript = (aoTerminar) => {
  const script = document.createElement('script')
  script.src = URL_DO_SDK
  script.async = true
  script.integrity = INTEGRIDADE
  script.crossOrigin = 'anonymous'
  script.addEventListener('load', () => {
    aoTerminar((typeof window !== 'undefined' && window[GLOBAL]) || null)
  })
  // `error` cobre rede E recusa por integridade — o browser não distingue os
  // dois para o consumidor, e o desfecho é o mesmo: sem vídeo.
  script.addEventListener('error', () => aoTerminar(null))
  return script
}

/**
 * Carrega o SDK e devolve o namespace dele.
 *
 * @returns {Promise<Object|null>} o `LivekitClient`, ou `null` quando não deu
 *   (sem DOM, script recusado pela integridade, rede, timeout). NUNCA rejeita:
 *   o vídeo é opcional por construção, e uma rejeição aqui derrubaria a
 *   conversa falada junto — que é a parte que importa.
 */
export const carregarLiveKit = () => {
  if (_carga) return _carga

  _carga = new Promise((resolve) => {
    const jaTem = typeof window !== 'undefined' && window[GLOBAL]
    if (typeof document === 'undefined' || !document.head || jaTem) {
      resolve(jaTem || null)
      return
    }

    let encerrado = false
    const encerrar = (valor) => {
      if (encerrado) return
      encerrado = true
      clearTimeout(prazo)
      resolve(valor)
    }
    const prazo = setTimeout(() => encerrar(null), MS_ATE_CARREGAR)

    document.head.appendChild(_montarScript(encerrar))
  })

  return _carga
}

/** Só para teste: esquece a carga em curso. Nunca chamado em produção. */
export const esquecerCargaDoSdk = () => {
  _carga = null
}
